import { useEffect } from "react";

// Import enums and route definitions
import { sidebarEmployeeOptionsEnum } from "../../../../enums/sidebarEmployeeOptionsEnum";
import routes from "../../../../Routes/routes";


import { permissionObj } from "../../../../Helpers/permission";
import { IconsSideBar } from "./IconsSideBar";
import {ROLE_NAME_DEFAULT_MODEL} from "../../../../enums/userTypeEnum";


/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

// Main component to manage the Employee sidebar menu options
const EmployeeTabsApp = ({ currentUrl, setChangeStyle, parts, permission, getUserByIdAtEntry }) => {
    // Determine the active module part based on the current URL path
    const modulePart = parts[3] ? parts[3] + (parts[4] ? '/' + parts[4] : '') : '';
    const roleName = getUserByIdAtEntry.roleName;
    // Set the sidebar style based on the current module path
    useEffect(() => {
        setChangeStyle(sidebarEmployeeOptionsEnum[modulePart]);
    }, [currentUrl]);

    // Define menu items with permissions and submenu items (if any)
    const menuItems = [
        {
            name: 'company',
            url: routes.company.update,
            label: 'company_profile',
            onlyName: true,
            icon: IconsSideBar.icCompany,
            isHovering: 1,
            hasPermission: permission?.includes(permissionObj.WEB_COMPANY_READ),
        },
        {
            name: 'zones',
            url: routes.zone.list,
            label: 'first_access',
            onlyName: true,
            icon: IconsSideBar.icFirstAccess,
            isHovering: 2,
            hasPermission: permission?.includes(permissionObj.WEB_ZONE_READ),
        },
        {
            name: 'devices',
            url: routes.devices.list,
            label: 'devices',
            onlyName: true,
            icon: IconsSideBar.icDevices,
            isHovering: 22,
            hasPermission: permission?.includes(permissionObj.WEB_DEVICE_READ),
        },
        {
            name: 'payroll',
            url: routes.attendance.list,
            label: 'attendance',
            onlyName: true,
            icon: IconsSideBar.icAttendance,
            isHovering: 8,
            hasPermission: permission?.includes(permissionObj.WEB_ATTENDANCE_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_SHIFT_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_SETTING_READ) ||
                permission?.includes(permissionObj.WEB_ATTENDANCE_DEPARTMENT_POLICIES_READ) ,
        },
        {
            name: 'departments',
            url: routes.department.list,
            label: 'departments',
            onlyName: true,
            icon: IconsSideBar.icDepartments,
            isHovering: 11,
            hasPermission: permission?.includes(permissionObj.WEB_DEPARTMENT_READ),
        },
        {
            name: 'employees',
            url: routes.employee.list,
            label: 'employees',
            onlyName: true,
            icon: IconsSideBar.icEmployees,
            isHovering: 4,
            hasPermission: permission?.includes(permissionObj.WEB_EMPLOYEE_READ),
        },
        {
            name: 'vehicles',
            url: routes.vehicleFleet.list,
            label: 'vehicle_fleet',
            onlyName: true,
            icon: IconsSideBar.icVehicleFleet,
            isHovering: 5,
            hasPermission: permission?.includes(permissionObj.WEB_VEHICLE_READ),
        },
        {
            name: 'reservations',
            url: routes.event.list,
            label: 'reservations',
            onlyName: true,
            icon: IconsSideBar.icEvents,
            isHovering: 3,
            hasPermission: permission?.includes(permissionObj.WEB_EVENT_READ) ||
                permission?.includes(permissionObj.WEB_EVENT_STATUS_READ) ||
                permission?.includes(permissionObj.WEB_EVENT_MANAGE_READ),
        },
        {
            name: 'controller',
            url: null,
            label: 'controller',
            onlyName: false,
            icon: IconsSideBar.icController,
            isHovering: 23,
            hasPermission: permission?.includes(permissionObj.WEB_HID_READ),
            itemsSubMenu: [
                { name: 'controller', url: routes.controller.list, icon: <IconsSideBar.icControllerSubmenu/> },
                { name: 'sios', url: routes.controller.sios, icon: <IconsSideBar.icSios/> },
                { name: 'readers', url: routes.controller.readers, icon: <IconsSideBar.icReaders/> },
                { name: 'inputs', url: routes.controller.inputs, icon: <IconsSideBar.icInput/> },
                { name: 'outputs', url: routes.controller.outputs, icon: <IconsSideBar.icOutput/> },
                { name: 'acrs', url: routes.controller.acrs, icon: <IconsSideBar.icAcr/> },
            ],
        },
        {
            name: 'workshift',
            url: routes.workshift.list,
            label: 'work_shift_panel',
            onlyName: true,
            icon: IconsSideBar.icWorkshipPanel,
            isHovering: 13,
            hasPermission: permission?.includes(permissionObj.WEB_WORK_SHIFT_READ),
        },
        {
            name: 'access',
            url: routes.access.list,
            label: 'access',
            onlyName: true,
            icon: IconsSideBar.icAccess,
            isHovering: 14,
            hasPermission:
                permission?.includes(permissionObj.WEB_ACCESS_USER_READ) ||
                permission?.includes(permissionObj.WEB_ACCESS_VEHICLE_READ),
        },
        {
            name: 'previlageSetting',
            url: routes.roles.list,
            label: 'privilage_settings',
            onlyName: true,
            icon: IconsSideBar.icPrivilageSettings,
            isHovering: 15,
            hasPermission: permission?.includes(permissionObj.WEB_PRIVILEGE_READ),
        },
        {
            name: 'onBoarding',
            url: routes.onboarding.list,
            label: 'on_boarding',
            onlyName: true,
            icon: IconsSideBar.icOnBoarding,
            isHovering: 16,
            hasPermission: permission?.includes(permissionObj.WEB_ONBOARDING_READ),
        },
        {
            name: 'contractor',
            url: routes.contractor.list,
            label: 'contractors',
            onlyName: true,
            icon: IconsSideBar.icContractors,
            isHovering: 6,
            hasPermission:
                permission?.includes(permissionObj.WEB_CONTRACTOR_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_CONTRACT_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_REQUEST_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_APPROVAL_FLOW_READ) ||
                permission?.includes(permissionObj.WEB_CONTRACTOR_MANAGE_READ),
        },
        {
            name: 'provider',
            url: routes.suppliers.main,
            label: 'supplier',
            onlyName: true,
            icon: IconsSideBar.icSupplier,
            isHovering: 7,
            hasPermission:
                permission?.includes(permissionObj.WEB_SUPPLIER_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_ORDER_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_APPROVAL_FLOW_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_REQUEST_READ) ||
                permission?.includes(permissionObj.WEB_SUPPLIER_MANAGE_READ),
        },
        {
            name: 'userDocument',
            url: routes.document.list,
            label: 'user_document_management',
            onlyName: true,
            icon: IconsSideBar.icDocumentPanel,
            isHovering: 17,
            hasPermission:
                permission?.includes(permissionObj.WEB_SUPPLIER_DOCUMENT_READ) ||
                permission?.includes(permissionObj.WEB_EMPLOYEE_DOCUMENT_READ)       },
       
        {
            name: 'emailSetting',
            url: routes.email.list,
            label: 'email_service_settings',
            onlyName: true,
            icon: IconsSideBar.icEmailServiceSettings,
            isHovering: 19,
            hasPermission: permission?.includes(permissionObj.WEB_EMAIL_SETTINGS_READ),
        },
        {
            name: 'backup',
            url: routes.backup.list,
            label: 'back_up',
            onlyName: true,
            icon: IconsSideBar.icBackUp,
            isHovering: 10,
            hasPermission: permission?.includes(permissionObj.WEB_BACK_UP_READ),
        },
        {
            name: 'homeoffice',
            url: routes.homeOffice.list,
            label: 'home_office',
            onlyName: true,
            icon: IconsSideBar.icHomeOffice,
            isHovering: 24,
            hasPermission: permission?.includes(permissionObj.WEB_HOME_OFFICE_READ),
        },
        {
            name: 'card_designer',
            url: routes.cardDesigner.list,
            label: 'card_designer',
            onlyName: true,
            icon: IconsSideBar.icCardDesigner,
            isHovering: 25,
            hasPermission: permission?.includes(permissionObj.WEB_CARD_DESIGNER_READ) ||
                permission?.includes(permissionObj.WEB_RESOURCES_READ),
        },
        {
            name: 'logs',
            url: routes.logPlatform.list,
            label: 'logs',
            onlyName: true,
            icon: IconsSideBar.icLogs,
            isHovering: 26,
            hasPermission: permission?.includes(permissionObj.WEB_LOG_PLATFORM_READ),
        },
        {
            name: 'announcements',
            url: routes.announcement.list,
            label: 'announcements',
            onlyName: true,
            icon: IconsSideBar.icAnnouncements,
            isHovering: 28,
            hasPermission: permission?.includes(permissionObj.WEB_ANNOUNCEMENT_READ),
        },
        {
            name: 'delete_and_restore',
            url: routes.hardDelete.list,
            label: 'delete_and_restore',
            icon: IconsSideBar.icDeleteRestore,
            onlyName: true,
            isHovering: 32,
            hasPermission: permission?.includes(permissionObj.WEB_HARD_DELETE_READ),
        },
        {
            name: 'graphs',
            url: routes.graphs.list,
            label: 'graphs',
            onlyName: true,
            icon: IconsSideBar.icGraphs,
            isHovering: 30,
            hasPermission: permission?.includes(permissionObj.WEB_CHART_READ),
        },
        {
            name: 'cards',
            url: routes.cards.list,
            label: 'card_management',
            onlyName: true,
            icon: IconsSideBar.icCardManagment,
            isHovering: 29,
            hasPermission: permission?.includes(permissionObj.WEB_CARD_READ),
        },
        {
            name: 'foodie_dining',
            url: routes.foodie.list,
            label: 'dining_management',
            onlyName: true,
            icon: IconsSideBar.icDiningManagement,
            isHovering: 53,
            hasPermission: permission?.includes(permissionObj.WEB_FOODIE_SHIFT_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_CAFETERIA_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MENU_READ) ||
                permission?.includes(permissionObj.WEB_INGREDIENT_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MEAL_TIME_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_ATTENDANCE_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_DEPARTMENT_POLICIES_READ) ||
                permission?.includes(permissionObj.WEB_TOTAL_MEALS_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MEAL_FORECAST_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_ACCESS_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_REQUEST_READ) ||
                permission?.includes(permissionObj.WEB_FOODIE_MANAGE_SETTINGS_READ) 
        },
        {
            name: 'foodie_orders',
            url: routes.foodieOrders.main,
            label: 'foodie_dining',
            onlyName: true,
            icon: IconsSideBar.icCafeteria,
            isHovering: 37,
            hasPermission: permission?.includes(permissionObj.WEB_FOODIE_MENU_SELECTION_VIEW) ||
                permission?.includes(permissionObj?.WEB_FOODIE_EXTRA_REQUEST_VIEW)
        },
        roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD &&
        {
            name: 'guard',
            url: routes.guard.list,
            label: 'guard',
            onlyName: true,
            icon: IconsSideBar.icGuard,
            isHovering: 27,
            hasPermission: permission?.includes(permissionObj.WEB_GUARD_READ),
        },
        {
            name: 'profile',
            url: routes.profile.list,
            label: 'profile',
            onlyName: true,
            icon: IconsSideBar.icProfile,
            isHovering: 21,
            hasPermission: permission?.includes(permissionObj.WEB_PROFILE_READ),
        },
        {
            name: 'systemConfig',
            url: routes.systemConfig.list,
            label: 'system_config',
            onlyName: true,
            icon: IconsSideBar.icSystemSettings,
            isHovering: 31,
            hasPermission: permission?.includes(permissionObj.WEB_SYSTEM_CONFIG_READ),
        },
        {
            name: 'distributionCenter',
            url: routes.distributionCenter.list,
            label: 'distribution_center',
            onlyName: true,
            icon: IconsSideBar.icDistributioCenter,
            isHovering: 33,
            hasPermission: permission?.includes(permissionObj.WEB_DISTRIBUTION_CENTER_READ),
        },
        {
            name: 'approval',
            url: routes.approval.list,
            label: 'APPROVAL_FLOW',
            onlyName: true,
            icon: IconsSideBar.icApprovalFlow,
            isHovering: 21,
            hasPermission: permission?.includes(permissionObj.WEB_APPROVAL_FLOW_READ),
        },
        {
            name: 'serviceType',
            url: routes.serviceType.list,
            label: 'service_type',
            onlyName: true,
            icon: IconsSideBar.icServiceType,
            isHovering: 34,
            hasPermission: permission?.includes(permissionObj.WEB_SERVICE_TYPE_READ)
        },
        {
            name: 'transportRequest',
            url: routes.transportRequest.list,
            label: 'transport_request',
            onlyName: true,
            icon: IconsSideBar.icTransportRequest,
            isHovering: 35,
            hasPermission: permission?.includes(permissionObj.WEB_LOGISTIC_REQUEST_READ),
        },
        {
            name: 'visitors',
            url: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD ? routes.guard.visitor : routes.visitor.list,
            label: 'visitor_management',
            onlyName: true,
            icon: IconsSideBar.icVisitorManagment,
            isHovering: 36,
            hasPermission: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD ?
                    (permission?.includes(permissionObj.WEB_VISITOR_READ) || permission?.includes(permissionObj.WEB_REQUEST_ACCESS_READ))
                :   (permission?.includes(permissionObj.WEB_VISITOR_INVITATION_READ) ||
                    permission?.includes(permissionObj.WEB_VISITOR_REQUEST_READ) ||
                    permission?.includes(permissionObj.WEB_VISITOR_READ) ||
                    permission?.includes(permissionObj.WEB_VISITOR_TYPE_READ) ||
                    permission?.includes(permissionObj.WEB_REQUEST_ACCESS_READ))
        },
        {
            name: 'incomingInvitations',
            url: routes.guard.incomingInvitation,
            label: 'incoming_invitations',
            onlyName: true,
            icon: IconsSideBar.icIncomingInvitation,
            isHovering: 37,
            hasPermission: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD &&
                permission?.includes(permissionObj.WEB_VISITOR_INCOMING_INVITATION_READ),
        },
        {
            name: 'trafficMainSystem',
            url: routes.traffic.list,
            label: 'traffic_main_system',
            onlyName: true,
            icon: IconsSideBar.icTraffic,
            isHovering: 38,
            hasPermission: permission?.includes(permissionObj?.WEB_FINE_TYPE_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_DASHBOARD_GEOFENCES_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_DASHBOARD_FINES_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_USERS_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_MANAGE_FINES_READ) ||
                permission?.includes(permissionObj?.WEB_TRAFFIC_MANAGE_GEOFENCES_READ),
        },
        {
            name: 'kitchen_tickets',
            url: routes.foodieKitchen.list,
            label: 'kitchen_tickets',
            onlyName: true,
            icon: IconsSideBar.icKitchenTickets,
            isHovering: 100,
            hasPermission: permission?.includes(permissionObj.WEB_KITCHEN_TICKETS_READ) || permission?.includes(permissionObj.WEB_KITCHEN_TICKETS_HISTORY_READ)
        },
        {
            name: 'incomingContractors',
            url: routes.guard.incomingContractors,
            label: 'incoming_contractors',
            onlyName: true,
            icon: IconsSideBar.icIncomingContractor,
            isHovering: 39,
            hasPermission: roleName === ROLE_NAME_DEFAULT_MODEL.ROLE_GUARD &&
                permission?.includes(permissionObj.WEB_CONTRACTOR_INCOMING_READ),
        },
          {
            name: 'delivered_services',
            url: routes.deliveredServices.list,
            label: 'delivered_services',
            onlyName: true,
            icon: IconsSideBar.icDeliveredServices,
            isHovering: 101,
            hasPermission: permission?.includes(permissionObj.WEB_FOODIE_KITCHEN_ACCESS_READ) || permission?.includes(permissionObj.WEB_FOODIE_KITCHEN_ACCESS_HISTORY_READ)
        },

    ];

    // Return the menu items for use in the UI
    return {
        menuItems
    };
}

export default EmployeeTabsApp; // Export this component for use in other parts of the application
